// Logging Setup
import { LoggerProvider, BatchLogRecordProcessor } from '@opentelemetry/sdk-logs';
import { OTLPLogExporter } from '@opentelemetry/exporter-logs-otlp-http';
import { logs } from '@opentelemetry/api-logs';

// Tracing Setup
import customSpanProcessor from './custom-processor';
import { WebTracerProvider } from '@opentelemetry/sdk-trace-web';
import { BatchSpanProcessor } from '@opentelemetry/sdk-trace-base';
import { OTLPTraceExporter } from '@opentelemetry/exporter-trace-otlp-http';
import { ZoneContextManager } from '@opentelemetry/context-zone';
import { CompositePropagator, W3CBaggagePropagator, W3CTraceContextPropagator } from '@opentelemetry/core';
import { SpanStatusCode } from '@opentelemetry/api';

// XHR Auto-Instrumentation
import { XMLHttpRequestInstrumentation } from '@opentelemetry/instrumentation-xml-http-request';
import { registerInstrumentations } from '@opentelemetry/instrumentation';

// Metrics Setup
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { metrics } from '@opentelemetry/api';

// Shared
import { resourceFromAttributes } from '@opentelemetry/resources';
import {
  ATTR_SERVICE_NAME,
  ATTR_SERVICE_VERSION
} from '@opentelemetry/semantic-conventions';

const PREFIX = '[telemetry]';

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;
const NUMERIC_ID_RE = /\/\d+(?=\/|$)/g;

const parameterizePath = (path: string): string => {
  return path.replace(UUID_RE, ':id').replace(NUMERIC_ID_RE, '/:id');
};

export interface TelemetryConfig {
  otelExporterEndpoint?: string;
  otelServiceName?: string;
  otelServiceVersion?: string;
  otelDeploymentEnvironment?: string;
  enableOtelTracing?: boolean;
  enableOtelMetrics?: boolean;
  enableOtelLogging?: boolean;
  [key: string]: unknown;
}

export function init(config: TelemetryConfig = {}): void {
  // No exporter endpoint configured → don't set up any OpenTelemetry at all.
  // No providers registered, no XHR auto-instrumentation, no fallback endpoint.
  const endpoint = config.otelExporterEndpoint;
  if (!endpoint) {
    console.warn(`${PREFIX} otelExporterEndpoint not set — OpenTelemetry disabled`);
    return;
  }

  const serviceName = config.otelServiceName || 'dx-web-ui';
  const environment = config.otelDeploymentEnvironment || 'localhost';

  // Read service version from version.json
  let serviceVersion = config.otelServiceVersion || '0.0.0';
  try {
    const xhr = new XMLHttpRequest();
    xhr.open('GET', '/version.json', false);
    xhr.send();
    if (xhr.status === 200) {
      const v = JSON.parse(xhr.responseText);
      serviceVersion = `${v.major}.${v.minor}.${v.build}`;
    }
  } catch { /* fallback to config */ }

  console.debug(`${PREFIX} initializing — service=${serviceName} endpoint=${endpoint}`);

  try {
    // Resource configuration
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: serviceName,
      [ATTR_SERVICE_VERSION]: serviceVersion,
      'deployment.environment': environment
    });

    // ============================================================================
    // Logging Configuration
    // ============================================================================

    if (config.enableOtelLogging !== false) {
      const loggerProvider = new LoggerProvider({
        resource,
        processors: [
          new BatchLogRecordProcessor(
            new OTLPLogExporter({
              url: `${endpoint}/v1/logs`
            })
          )
        ]
      });

      logs.setGlobalLoggerProvider(loggerProvider);
      console.debug(`${PREFIX} logging provider registered`);
    } else {
      console.debug(`${PREFIX} logging disabled`);
    }

    // ============================================================================
    // Tracing Configuration
    // ============================================================================

    if (config.enableOtelTracing !== false) {
      const traceExporter = new OTLPTraceExporter({
        url: `${endpoint}/v1/traces`
      });

      const tracerProvider = new WebTracerProvider({
        resource,
        spanProcessors: [
          new BatchSpanProcessor(traceExporter),
          customSpanProcessor
        ]
      });

      // Register with explicit W3C propagator for distributed tracing.
      tracerProvider.register({
        contextManager: new ZoneContextManager(),
        propagator: new CompositePropagator({
          propagators: [new W3CTraceContextPropagator(), new W3CBaggagePropagator()]
        })
      });
      console.debug(`${PREFIX} tracer provider registered`);

      // The BatchSpanProcessor exports every 5 seconds and uses fetch with keepalive
      // (which survives page unload). No additional flush listeners needed.

      // ============================================================================
      // XHR Auto-Instrumentation
      // ============================================================================

      // XMLHttpRequestInstrumentation handles span lifecycle at the XHR level —
      // spans are reliably ended on load/error/timeout/abort (no leaks).
      // Capture method AND url at open() so applyCustomAttributesOnSpan can rename
      // even when the request never completes (responseURL is "" on abort/timeout/network error).
      // Also pin which failure event fired so we can flag silent cancellations as errors —
      // the instrumentation's own status==0 handling is unreliable across versions and
      // page-navigation aborts (the silent 19s case) often slip through with has_error=false.
      const origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method: string, url: string | URL, ...rest: unknown[]) {
        (this as any).__httpMethod = method;
        (this as any).__httpUrl = typeof url === 'string' ? url : url.toString();
        this.addEventListener('abort', () => { (this as any).__failureType = 'aborted'; });
        this.addEventListener('timeout', () => { (this as any).__failureType = 'timeout'; });
        this.addEventListener('error', () => { (this as any).__failureType = 'network_error'; });
        return (origOpen as Function).apply(this, [method, url, ...rest]);
      };

      registerInstrumentations({
        instrumentations: [
          new XMLHttpRequestInstrumentation({
            ignoreUrls: [/v1\/(traces|logs|metrics)/, /\.html($|\?)/],
            // Propagate traceparent/tracestate to ALL origins (not just same-origin)
            // so distributed tracing works with cross-origin API servers.
            propagateTraceHeaderCorsUrls: [/.*/],
            applyCustomAttributesOnSpan(span, xhr) {
              try {
                const method = ((xhr as any).__httpMethod || 'GET').toUpperCase();
                // responseURL is empty when the request was aborted/timed out/failed
                // before a response was received. Fall back to the URL captured at open().
                const rawUrl = xhr.responseURL || (xhr as any).__httpUrl || '';
                const pathname = new URL(rawUrl, window.location.origin).pathname;
                const route = parameterizePath(decodeURIComponent(pathname).replace(/\+/g, ''));
                span.updateName(`${method} ${route}`);
              } catch { /* keep default name */ }

              // Surface silent client-side failures (abort/timeout/network) as errors.
              // status===0 with a captured failure event means the request never produced
              // an HTTP response; without this they ship with has_error=false and disappear
              // from error dashboards. Runs before the 4xx suppression below so the shadow
              // doesn't block us.
              const failureType = (xhr as any).__failureType;
              if (xhr.status === 0 && failureType) {
                span.setStatus({ code: SpanStatusCode.ERROR, message: failureType });
                span.setAttribute('error.type', failureType);
              }

              // 5xx responses get tagged with the status code as error.type. The XHR
              // auto-instrumentation flags ERROR status for >=400 but doesn't write
              // this attribute, so dashboards filtering by error.type miss server errors.
              if (xhr.status >= 500) {
                span.setAttribute('error.type', String(xhr.status));
              }

              // Only treat 5xx responses as errors. The XHR instrumentation auto-sets
              // ERROR status for any status >= 400 right after this callback returns,
              // so we shadow setStatus/setAttribute on this span to suppress it for 4xx.
              if (xhr.status >= 400 && xhr.status < 500) {
                const originalSetStatus = span.setStatus.bind(span);
                const originalSetAttribute = span.setAttribute.bind(span);
                span.setStatus = (status) => {
                  if (status && status.code === SpanStatusCode.ERROR) return span;
                  return originalSetStatus(status);
                };
                span.setAttribute = (key, value) => {
                  if (key === 'error.type') return span;
                  return originalSetAttribute(key, value);
                };
              }
            }
          })
        ]
      });
      console.debug(`${PREFIX} XHR auto-instrumentation registered`);
    } else {
      console.debug(`${PREFIX} tracing disabled`);
    }

    // ============================================================================
    // Metrics Configuration
    // ============================================================================

    if (config.enableOtelMetrics !== false) {
      const metricReader = new PeriodicExportingMetricReader({
        exporter: new OTLPMetricExporter({
          url: `${endpoint}/v1/metrics`
        }),
        exportIntervalMillis: 300000 // Export metrics every 5 minutes.
      });

      const meterProvider = new MeterProvider({
        resource,
        readers: [metricReader]
      });

      metrics.setGlobalMeterProvider(meterProvider);
      console.debug(`${PREFIX} metrics provider registered`);
    } else {
      console.debug(`${PREFIX} metrics disabled`);
    }

    console.debug(`${PREFIX} initialization complete`);
  } catch (err) {
    console.error(`${PREFIX} initialization FAILED:`, err);
  }
}